import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Compass, Wand2, Sparkles, ArrowRight, Flame, Layers } from "lucide-react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Spinner } from "@/components/Bits";

export default function PlanGallery() {
  const navigate = useNavigate();
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get(`/autopilot/gallery`)
      .then(({ data }) => setPlans(data.plans || []))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  return (
    <div className="max-w-4xl mx-auto px-4 py-6" data-testid="plan-gallery">
      <div className="flex items-center gap-3 mb-1">
        <div className="w-11 h-11 rounded-2xl grid place-items-center bg-gradient-to-br from-[#1f6feb] to-[#a855f7] shrink-0">
          <Compass className="w-5 h-5 text-white" />
        </div>
        <div>
          <h1 className="font-display text-xl font-extrabold leading-tight">Plan Gallery</h1>
          <p className="text-sm text-[hsl(var(--muted-foreground))]">Community Director plans, most-remixed first — pick one and run it on your own game.</p>
        </div>
      </div>

      {loading ? (
        <div className="min-h-[40vh] grid place-items-center"><Spinner /></div>
      ) : plans.length === 0 ? (
        <div className="text-center py-16" data-testid="plan-gallery-empty">
          <div className="w-14 h-14 rounded-2xl grid place-items-center bg-[hsl(var(--secondary))] mx-auto mb-4"><Wand2 className="w-6 h-6 text-[hsl(var(--muted-foreground))]" /></div>
          <h2 className="font-display text-lg font-bold">No shared plans yet</h2>
          <p className="text-sm text-[hsl(var(--muted-foreground))] mt-2">Be the first — draft a plan in the AI Game Director and hit Share.</p>
          <Button onClick={() => navigate("/app/games")} className="rounded-full mt-5" data-testid="plan-gallery-mygames">Go to my games</Button>
        </div>
      ) : (
        <div className="grid sm:grid-cols-2 gap-3 mt-5">
          {plans.map((p) => (
            <button key={p.code} onClick={() => navigate(`/app/plan/${p.code}`)} data-testid={`gallery-card-${p.code}`}
              className="text-left rounded-2xl border border-[hsl(var(--border))] bg-[hsl(var(--card))] p-4 hover:border-[hsl(var(--primary))]/60 hover:shadow-lg hover:shadow-black/5 transition-all group">
              <div className="flex items-center gap-2 mb-2">
                <span className="text-lg">{p.cover_emoji}</span>
                <span className="text-[11px] text-[hsl(var(--muted-foreground))] truncate">from {p.game_title}</span>
                <span className="ml-auto inline-flex items-center gap-1 text-[10px] font-bold text-[hsl(var(--primary))]" data-testid={`gallery-remix-${p.code}`} title="Times remixed">
                  <Flame className="w-3 h-3" /> {p.remix_count}
                </span>
              </div>
              <p className="text-sm font-semibold leading-snug line-clamp-2">{p.goal}</p>
              {p.overview && <p className="text-[11px] text-[hsl(var(--muted-foreground))] mt-1.5 line-clamp-2">{p.overview}</p>}
              <div className="flex items-center justify-between mt-3 pt-3 border-t border-[hsl(var(--border))]">
                <span className="inline-flex items-center gap-1 text-[11px] text-[hsl(var(--muted-foreground))]"><Layers className="w-3.5 h-3.5" /> {p.step_count} steps</span>
                <span className="inline-flex items-center gap-1 text-[11px] font-semibold text-[hsl(var(--primary))] group-hover:gap-2 transition-all">
                  <Sparkles className="w-3.5 h-3.5" /> Remix <ArrowRight className="w-3.5 h-3.5" />
                </span>
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
